import React, { forwardRef, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  fieldRequired,
  requiredInput,
  requiredSelect,
  requiredTextArea,
} from '../../../core/formValidation';
import { ActionDisplayFormats, FeedbackType, PriorityType } from '../models';

interface FeedbackFormValues {
  customerName: string;
  comment: string;
  type: number;
  priority: number;
  actionTaken: any;
}

interface Props {
  onSubmit?: (values: FeedbackFormValues) => void;
  initialValues?: Partial<FeedbackFormValues>;
}

const FeedbackEditForm = forwardRef<{ submit: () => void }, Props>(
  ({ onSubmit, initialValues = {} }, ref) => {
    const intl = useIntl();
    const { control, handleSubmit } = useForm<FeedbackFormValues>({
      defaultValues: {
        customerName: '',
        comment: '',
        type: FeedbackType.Positive,
        priority: PriorityType.Low,
        actionTaken: 0,
        ...initialValues,
      },
    });

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit((vals) => onSubmit && onSubmit(vals))(),
    }));

    return (
      <div>
        <div className="form-group">
          <label htmlFor="customerName">{intl.formatMessage({ id: 'customerName' })}</label>
          <Controller
            name="customerName"
            control={control}
            rules={{ validate: (v) => !fieldRequired(v) || 'Required' }}
            render={({ field }) =>
              requiredInput({
                input: field,
                id: 'customerName',
                type: 'text',
                className: 'form-control',
                placeholder: "Enter customer's full name",
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="comment">{intl.formatMessage({ id: 'comment' })}</label>
          <Controller
            name="comment"
            control={control}
            rules={{ validate: (v) => !fieldRequired(v) || 'Required' }}
            render={({ field }) =>
              requiredTextArea({
                input: field,
                id: 'comment',
                className: 'form-control',
                maxLength: '255',
                placeholder: 'Enter comment',
                'data-testid': 'comment-feedback-form',
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="type">{intl.formatMessage({ id: 'feedbackType' })}</label>
          <Controller
            name="type"
            control={control}
            rules={{ validate: (v) => !fieldRequired(v) || 'Required' }}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e: any) => onChange(Number(e?.target?.value ?? e)),
                },
                id: 'type',
                className: 'form-control',
                children: (
                  <>
                    <option value={FeedbackType.Positive}>
                      {intl.formatMessage({ id: 'positive' })}
                    </option>
                    <option value={FeedbackType.Neutral}>
                      {intl.formatMessage({ id: 'neutral' })}
                    </option>
                    <option value={FeedbackType.Negative}>
                      {intl.formatMessage({ id: 'negative' })}
                    </option>
                  </>
                ),
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="priority">Priority</label>
          <Controller
            name="priority"
            control={control}
            rules={{ validate: (v) => !fieldRequired(v) || 'Required' }}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e: any) => onChange(Number(e?.target?.value ?? e)),
                },
                id: 'priority',
                className: 'form-control',
                children: (
                  <>
                    <option value={PriorityType.Low}>{intl.formatMessage({ id: 'low' })}</option>
                    <option value={PriorityType.Medium}>
                      {intl.formatMessage({ id: 'medium' })}
                    </option>
                    <option value={PriorityType.High}>{intl.formatMessage({ id: 'high' })}</option>
                  </>
                ),
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="actionTaken">{intl.formatMessage({ id: 'actionTaken' })}</label>
          <Controller
            name="actionTaken"
            control={control}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e: any) => onChange(Number(e?.target?.value ?? e)),
                },
                id: 'actionTaken',
                className: 'form-control',
                children: (
                  <>
                    <option value={ActionDisplayFormats.NoneNeeded}>
                      {intl.formatMessage({ id: 'noneNeeded' })}
                    </option>
                    <option value={ActionDisplayFormats.Resolved}>
                      {intl.formatMessage({ id: 'resolved' })}
                    </option>
                    <option value={ActionDisplayFormats.NotYetResolved}>
                      {intl.formatMessage({ id: 'notYetResolved' })}
                    </option>
                  </>
                ),
              })
            }
          />
        </div>
      </div>
    );
  }
);

export default FeedbackEditForm;
